import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownWideNarrow, Eye, Search } from "lucide-react";
import View from "./View";

const c = {
  greenDk: "#1C4A2E",
  greenMd: "#2D6B45",
  gold: "#E8A020",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
};

interface Pedido {
  pedido_id: number;
  fecha_creacion: string;
  estado: string;
}

type EstadoFilter = "ambos" | "Pendiente" | "En camino";
type SortMode = "fechaDesc" | "fechaAsc";

const FILTER_PILLS: { id: EstadoFilter; label: string }[] = [
  { id: "ambos", label: "Todos" },
  { id: "Pendiente", label: "Pendiente" },
  { id: "En camino", label: "En camino" },
];

function formatDate(dateString: string) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${d}/${m}/${y}`;
}

function estadoAccent(estado: string) {
  const e = (estado || "").toLowerCase();
  if (e.includes("pend")) return c.gold;
  if (e.includes("camino") || e.includes("enví")) return c.greenMd;
  return c.muted;
}

const PedidosList = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [pedidoView, setPedidoView] = useState(false);
  const [pedidoViewID, setPedidoViewID] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>("ambos");
  const [sortMode, setSortMode] = useState<SortMode>("fechaDesc");
  const [openId, setOpenId] = useState<number | null>(null);

  const getPedidos = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/pedidos/usuario/${sessionStorage.getItem("user_id")}`,
        {
          headers: {
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      const data = response.data;
      setPedidos(Array.isArray(data) ? data : []);
    } catch {
      setPedidos([]);
    }
  }, []);

  useEffect(() => {
    getPedidos();
  }, [getPedidos]);

  const visible = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    let list = pedidos.filter((p) => {
      if (estadoFilter !== "ambos" && p.estado !== estadoFilter) {
        return false;
      }
      if (q) {
        const idStr = String(p.pedido_id);
        const fechaStr = formatDate(p.fecha_creacion);
        const hay = `${idStr} ${(p.estado || "").toLowerCase()} ${fechaStr}`.toLowerCase();
        if (!hay.includes(q) && !idStr.includes(q)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      const ta = new Date(a.fecha_creacion).getTime();
      const tb = new Date(b.fecha_creacion).getTime();
      if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
      return sortMode === "fechaDesc" ? tb - ta : ta - tb;
    });
    return list;
  }, [pedidos, searchTerm, estadoFilter, sortMode]);

  const toggleSort = () => {
    setSortMode((m) => (m === "fechaDesc" ? "fechaAsc" : "fechaDesc"));
  };

  const openDetail = (id: number) => {
    setPedidoViewID(id);
    setPedidoView(true);
  };

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <div
        className="mx-auto flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden md:max-w-6xl md:rounded-xl md:border md:border-gray-200/90 md:shadow-sm lg:max-w-7xl"
        style={{ background: c.surface }}
      >
        <div className="flex-shrink-0 border-b border-black/[0.06] bg-white px-3.5 pb-2.5 pt-3 md:px-6 md:pt-4">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
              style={{ color: c.muted }}
            />
            <input
              type="search"
              placeholder="Buscar por # pedido, fecha o estado…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border-[1.5px] py-2.5 pl-8 pr-3 text-sm text-[0.8rem] text-[#0F1F17] outline-none transition focus:border-[#2D6B45] focus:ring-[3px] focus:ring-[#2D6B45]/10"
              style={{
                borderColor: c.border,
                background: c.white,
              }}
            />
          </div>
          <div
            className="mt-2.5 flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] md:flex-wrap md:overflow-x-visible [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Filtro por estado"
          >
            {FILTER_PILLS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={estadoFilter === f.id}
                onClick={() => {
                  setEstadoFilter(f.id);
                  setOpenId(null);
                }}
                className="flex-shrink-0 rounded-full border border-[1.5px] px-3 py-1.5 text-[0.7rem] font-semibold transition"
                style={{
                  borderColor: estadoFilter === f.id ? c.greenMd : c.border,
                  background: estadoFilter === f.id ? c.greenMd : "transparent",
                  color: estadoFilter === f.id ? c.white : c.muted,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center justify-between px-3.5 py-0.5 md:px-6">
          <span
            className="text-[0.58rem] font-semibold uppercase tracking-widest sm:text-xs"
            style={{ color: c.muted }}
          >
            {visible.length} {visible.length === 1 ? "pedido" : "pedidos"}
          </span>
          <button
            type="button"
            onClick={toggleSort}
            className="inline-flex items-center gap-1.5 text-[0.6rem] font-semibold sm:text-xs"
            style={{ color: c.greenMd }}
          >
            <ArrowDownWideNarrow className="h-3.5 w-3.5" />
            {sortMode === "fechaDesc"
              ? "Fecha (más reciente)"
              : "Fecha (más antigua)"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3 md:px-6 md:pb-4 flex flex-col gap-1.5 md:grid md:grid-cols-2 md:content-start md:gap-3 lg:grid-cols-3">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center md:col-span-full">
              <Search className="h-9 w-9" style={{ color: c.muted }} aria-hidden />
              <p className="mt-1.5 text-sm font-bold" style={{ color: c.ink }}>
                {pedidos.length === 0 ? "Sin pedidos" : "Sin resultados"}
              </p>
              <p className="mt-1 text-xs leading-relaxed" style={{ color: c.muted }}>
                {pedidos.length === 0
                  ? "Aún no registramos pedidos en tu cuenta."
                  : "No hay pedidos que coincidan con tu búsqueda o filtro."}
              </p>
            </div>
          ) : (
            visible.map((pedido, idx) => {
              const open = openId === pedido.pedido_id;
              return (
                <div
                  key={pedido.pedido_id}
                  className="h-fit min-w-0 overflow-hidden rounded-2xl border border-[1.5px] bg-white transition"
                  style={{
                    borderColor: c.border,
                    boxShadow: open ? "0 4px 16px rgba(0,0,0,0.07)" : undefined,
                    animation: `pedidoSlide 0.28s ease both`,
                    animationDelay: `${Math.min(idx, 12) * 0.04}s`,
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenId((id) => (id === pedido.pedido_id ? null : pedido.pedido_id))
                    }
                    className="flex w-full min-w-0 items-start gap-3 px-3.5 py-2.5 text-left transition hover:-translate-y-px"
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="mb-0.5 inline-block rounded bg-[#E8EDEA] px-1.5 py-px text-[0.5rem] font-bold uppercase tracking-widest"
                        style={{ color: c.muted }}
                      >
                        #{pedido.pedido_id}
                      </div>
                      <div
                        className="break-words text-[0.8125rem] font-bold leading-snug"
                        style={{ color: c.ink }}
                      >
                        {formatDate(pedido.fecha_creacion)}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                      <div
                        className="flex max-w-[9rem] items-center justify-end gap-0.5 text-xs font-extrabold"
                        style={{ color: estadoAccent(pedido.estado) }}
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: estadoAccent(pedido.estado) }}
                        />
                        <span className="line-clamp-2 break-words">
                          {pedido.estado}
                        </span>
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div
                      className="space-y-2 border-t border-[rgba(28,74,46,0.1)] px-3.5 py-2.5"
                      style={{ background: c.surface }}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium" style={{ color: c.muted }}>
                          N.º de pedido
                        </span>
                        <span className="font-bold" style={{ color: c.ink }}>
                          {pedido.pedido_id}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium" style={{ color: c.muted }}>
                          Fecha
                        </span>
                        <span className="font-bold" style={{ color: c.ink }}>
                          {formatDate(pedido.fecha_creacion)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium" style={{ color: c.muted }}>
                          Estado
                        </span>
                        <span
                          className="font-bold"
                          style={{ color: estadoAccent(pedido.estado) }}
                        >
                          {pedido.estado}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(pedido.pedido_id);
                        }}
                        className="mt-0.5 flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-[0.78rem] font-bold text-white transition hover:opacity-95"
                        style={{ background: c.greenMd }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Ver detalle
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <View
        pedidoView={pedidoView}
        setPedidoView={setPedidoView}
        pedidoViewID={pedidoViewID}
      />

      <style>
        {`
        @keyframes pedidoSlide {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: none; }
        }
        `}
      </style>
    </div>
  );
};

export default PedidosList;
